import { withAuthenticationRequired } from "@auth0/auth0-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Grid,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CONFIG } from "../config";

function APIKeyCreate() {
  const navigate = useNavigate();
  const establishments = useSelector((state) => state.establishments);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    establishment_id: "",
    environment: "live",
    rate_limit_tier: "standard",
    scopes: [],
  });

  const [availableScopes] = useState([
    "read:establishments",
    "read:residences",
    "read:devices",
    "read:consumption",
    "write:occupancy",
  ]);

  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleScopeToggle = (scope) => {
    const newScopes = formData.scopes.includes(scope)
      ? formData.scopes.filter((s) => s !== scope)
      : [...formData.scopes, scope];
    setFormData({ ...formData, scopes: newScopes });
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.name || !formData.establishment_id || formData.scopes.length === 0) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedKey(data);
        setShowKeyDialog(true);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Error creating API Key");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("API Key copied to clipboard!");
  };

  const handleClose = () => {
    setShowKeyDialog(false);
    navigate("/api-keys");
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Create New API Key
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g., PMS Integration - Production"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Optional description for this API key"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Establishment *</InputLabel>
                <Select
                  value={formData.establishment_id}
                  onChange={(e) => handleChange("establishment_id", e.target.value)}
                  label="Establishment *"
                >
                  {establishments.map((est) => (
                    <MenuItem key={est.id} value={est.id}>
                      {est.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Environment</InputLabel>
                <Select
                  value={formData.environment}
                  onChange={(e) => handleChange("environment", e.target.value)}
                  label="Environment"
                >
                  <MenuItem value="test">Test</MenuItem>
                  <MenuItem value="live">Live (Production)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Rate Limit Tier</InputLabel>
                <Select
                  value={formData.rate_limit_tier}
                  onChange={(e) => handleChange("rate_limit_tier", e.target.value)}
                  label="Rate Limit Tier"
                >
                  <MenuItem value="standard">Standard (10,000/day)</MenuItem>
                  <MenuItem value="premium">Premium (100,000/day)</MenuItem>
                  <MenuItem value="enterprise">Enterprise (1,000,000/day)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Scopes (Permissions) *
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {availableScopes.map((scope) => (
                  <Chip
                    key={scope}
                    label={scope}
                    onClick={() => handleScopeToggle(scope)}
                    color={formData.scopes.includes(scope) ? "primary" : "default"}
                    variant={formData.scopes.includes(scope) ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  Create API Key
                </Button>
                <Button variant="outlined" onClick={() => navigate("/api-keys")}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Dialog pour afficher la clé générée */}
      <Dialog open={showKeyDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          API Key Created Successfully
          <Typography variant="body2" color="text.secondary">
            ⚠️ Save this key now - it will only be shown once!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Make sure to copy your API key now. You won't be able to see it again!
          </Alert>

          {generatedKey && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  API Key:
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#2c2c2c", color: "#ffffff",
                    borderRadius: 1,
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ flex: 1, fontSize: "0.9rem" }}>
                    {generatedKey.full_key}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(generatedKey.full_key)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary">
                <strong>Name:</strong> {generatedKey.api_key.name}
                <br />
                <strong>Establishment:</strong> {generatedKey.api_key.establishment_name}
                <br />
                <strong>Environment:</strong> {generatedKey.api_key.environment}
                <br />
                <strong>Rate Limit:</strong> {generatedKey.api_key.requests_per_day} requests/day
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default withAuthenticationRequired(APIKeyCreate);
